import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DeleteModal from "../../components/Common/DeleteModal";
import * as Yup from "yup";
import { useFormik } from "formik";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import withAuth from "../withAuth";
import axios from "axios";
import { handleAuthError } from "../../utils";
import {
    Col,
    Container,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Label,
    Input,
    FormFeedback,
    Card,
    CardBody,
    CardHeader,
    Badge,
    Progress
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';
import { usePagination } from "../../hooks/usePagination";
import PaginationControls from "../../components/Common/PaginationControls";

const PartnerReport = () => {
    document.title = "Evaluation Reports | RYD Admin";

    const { id } = useParams(); // Partner ID from URL params
    const [evaluationData, setEvaluationData] = useState(null);
    const [children, setChildren] = useState([]);
    const [filteredChildren, setFilteredChildren] = useState([]);
    const [selectedChild, setSelectedChild] = useState({});
    const [detailsModal, setDetailsModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    const {
        paginatedData: paginatedChildren,
        currentPage,
        totalPages,
        totalItems,
        pageSize,
        showAll,
        handlePageChange,
        handlePageSizeChange,
        goToNextPage,
        goToPreviousPage,
        canPreviousPage,
        canNextPage,
        paginatedDataLength,
    } = usePagination(filteredChildren, 20);

    useEffect(() => {
        fetchEvaluationData();
    }, []);

    useEffect(() => {
        filterChildren();
    }, [children, searchQuery]);

    const fetchEvaluationData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${baseUrl}/admin/partner/evaluations/${id}`);
            if (response.data.status) {
                setEvaluationData(response.data.data);
                setChildren(response.data.data.children || []);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            handleAuthError(error);
            console.error(error);
            toast.error("Failed to load evaluation data");
        } finally {
            setLoading(false);
        }
    };

    const filterChildren = () => {
        const filtered = children.filter(child =>
            `${child.firstName} ${child.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
            child.teacher?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            child.level?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredChildren(filtered);
    };

    const openChildDetails = (child) => {
        setSelectedChild(child);
        setDetailsModal(true);
    };

    const closeChildDetails = () => {
        setDetailsModal(false);
        setSelectedChild({});
    };

    const getPerformanceColor = (percentage) => {
        if (percentage >= 80) return "success";
        if (percentage >= 60) return "warning";
        if (percentage >= 40) return "info";
        return "danger";
    };

    const getStatusColor = (isActive) => {
        return isActive ? "success" : "secondary";
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Dashboard" breadcrumbItem="Partner Evaluation Report" />
                    <div>
                        <Link to="/manage-partner">
                            <button
                                className="btn btn-primary mb-3"
                            >
                                <i className="mdi mdi-arrow-left me-1"></i>
                                Manage Partners
                            </button>
                        </Link>
                    </div>
                    {/* Overall Statistics Cards */}
                    {evaluationData && (
                        <Row className="mb-4">
                            <Col xl={3} md={6}>
                                <Card>
                                    <CardBody>
                                        <div className="d-flex">
                                            <div className="flex-1">
                                                <p className="text-truncate font-size-14 mb-2">Total Children</p>
                                                <h4 className="mb-2">{evaluationData.overallStats.totalChildren}</h4>
                                                <p className="text-muted mb-0">
                                                    <span className="text-success fw-bold font-size-12 me-2">
                                                        <i className="ri-arrow-right-up-line me-1 align-middle"></i>
                                                        {evaluationData.overallStats.activeChildren} Active
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="avatar-sm">
                                                <span className="avatar-title bg-light text-primary rounded-3">
                                                    <i className="mdi mdi-account-multiple font-size-24"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col xl={3} md={6}>
                                <Card>
                                    <CardBody>
                                        <div className="d-flex">
                                            <div className="flex-1">
                                                <p className="text-truncate font-size-14 mb-2">Average Completion</p>
                                                <h4 className="mb-2">{evaluationData.overallStats.averageCompletion}%</h4>
                                                <p className="text-muted mb-0">
                                                    <span className="text-info fw-bold font-size-12 me-2">
                                                        <i className="ri-bar-chart-line me-1 align-middle"></i>
                                                        Progress Rate
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="avatar-sm">
                                                <span className="avatar-title bg-light text-info rounded-3">
                                                    <i className="mdi mdi-chart-line font-size-24"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col xl={3} md={6}>
                                <Card>
                                    <CardBody>
                                        <div className="d-flex">
                                            <div className="flex-1">
                                                <p className="text-truncate font-size-14 mb-2">Average Score</p>
                                                <h4 className="mb-2">{evaluationData.overallStats.averageScore.toFixed(1)}</h4>
                                                <p className="text-muted mb-0">
                                                    <span className="text-warning fw-bold font-size-12 me-2">
                                                        <i className="ri-star-line me-1 align-middle"></i>
                                                        Performance
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="avatar-sm">
                                                <span className="avatar-title bg-light text-warning rounded-3">
                                                    <i className="mdi mdi-trophy font-size-24"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col xl={3} md={6}>
                                <Card>
                                    <CardBody>
                                        <div className="d-flex">
                                            <div className="flex-1">
                                                <p className="text-truncate font-size-14 mb-2">Total Evaluations</p>
                                                <h4 className="mb-2">{evaluationData.overallStats.totalEvaluations}</h4>
                                                <p className="text-muted mb-0">
                                                    <span className="text-success fw-bold font-size-12 me-2">
                                                        <i className="ri-file-list-3-line me-1 align-middle"></i>
                                                        Completed
                                                    </span>
                                                </p>
                                            </div>
                                            <div className="avatar-sm">
                                                <span className="avatar-title bg-light text-success rounded-3">
                                                    <i className="mdi mdi-file-document font-size-24"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Best Performing Child Alert */}
                    {evaluationData?.overallStats?.bestPerformingChild && (
                        <Row className="mb-4">
                            <Col>
                                <div className="alert alert-success d-flex align-items-center" role="alert">
                                    <i className="mdi mdi-trophy font-size-20 me-2"></i>
                                    <div>
                                        <strong>Best Performer:</strong> {evaluationData.overallStats.bestPerformingChild.firstName} {evaluationData.overallStats.bestPerformingChild.lastName}
                                        <span className="badge badge-soft-success ms-2">
                                            {evaluationData.overallStats.bestPerformingChild.scorePercentage}% Score
                                        </span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Needs Attention Alert */}
                    {evaluationData?.overallStats?.needsAttention?.length > 0 && (
                        <Row className="mb-4">
                            <Col>
                                <div className="alert alert-warning d-flex align-items-center" role="alert">
                                    <i className="mdi mdi-alert font-size-20 me-2"></i>
                                    <div>
                                        <strong>Needs Attention:</strong> {evaluationData.overallStats.needsAttention.length} children require support
                                        <div className="mt-1">
                                            {evaluationData.overallStats.needsAttention.slice(0, 3).map((child, index) => (
                                                <span key={index} className="badge badge-soft-warning me-1">
                                                    {child.firstName} {child.lastName}
                                                </span>
                                            ))}
                                            {evaluationData.overallStats.needsAttention.length > 3 && (
                                                <span className="text-muted">+{evaluationData.overallStats.needsAttention.length - 3} more</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="mb-3">
                                <h5 className="card-title">
                                    Children Performance Report
                                    {evaluationData && (
                                        <span className="text-muted fw-normal ms-2">
                                            ({filteredChildren.length}) - {evaluationData.partner.name}
                                        </span>
                                    )}
                                </h5>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                                <div style={{ width: 200 }}>
                                    <Input
                                        type="text"
                                        placeholder="Search children..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <button
                                        onClick={fetchEvaluationData}
                                        className="btn btn-primary"
                                        disabled={loading}
                                    >
                                        <i className="mdi mdi-refresh me-1"></i>
                                        Refresh
                                    </button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col lg="12">
                            <Row>
                                <Col xl="12">
                                    {loading ? (
                                        <div className="text-center mt-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-3 text-muted">Loading evaluation data...</p>
                                        </div>
                                    ) : !evaluationData ? (
                                        <Card>
                                            <CardBody className="text-center py-5">
                                                <i className="mdi mdi-file-document-outline font-size-48 text-muted mb-3"></i>
                                                <h4 className="mb-2">No Data Available</h4>
                                                <p className="text-muted">No evaluation data found for this partner</p>
                                            </CardBody>
                                        </Card>
                                    ) : filteredChildren.length === 0 ? (
                                        <Card>
                                            <CardBody className="text-center py-5">
                                                <i className="mdi mdi-magnify font-size-48 text-muted mb-3"></i>
                                                <h4 className="mb-2">No Report for this partner children</h4>
                                                <p className="text-muted">Or try adjusting your search criteria</p>
                                            </CardBody>
                                        </Card>
                                    ) : (
                                        <table className="table align-middle">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Child Name</th>
                                                    <th>Teacher</th>
                                                    <th>Level</th>
                                                    <th>Status</th>
                                                    <th>Total Score</th>
                                                    <th>Attendance</th>
                                                    <th>Assignment</th>
                                                    <th>Project</th>
                                                    <th>Performance</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedChildren.map((child, index) => {
                                                    const globalIndex = showAll 
                                                        ? index 
                                                        : (currentPage - 1) * pageSize + index;
                                                    return (
                                                        <tr key={child.childId || index}>
                                                            <td>{globalIndex + 1}</td>

                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div>
                                                                    <h5 className="font-size-14 mb-1">
                                                                        {child.firstName} {child.lastName}
                                                                    </h5>
                                                                    <p className="text-muted font-size-12 mb-0">{child.gender}</p>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td>{child.teacher?.name || "N/A"}</td>

                                                        <td>
                                                            <Badge className="badge-light-info">
                                                                {child.level}
                                                            </Badge>
                                                        </td>

                                                        <td>
                                                            <Badge color={`${getStatusColor(child.isActive)}`}>
                                                                {child.isActive ? "Active" : "Inactive"}
                                                            </Badge>
                                                        </td>

                                                        <td>
                                                            <div className="text-center">
                                                                <span className="font-size-14 fw-bold text-primary">
                                                                    {child.totalScore}
                                                                </span>
                                                                <span className="text-muted">/{child.totalPossibleScore}</span>
                                                            </div>
                                                        </td>

                                                        <td>
                                                            <Badge color="badge-soft-success" className="badge-soft-success">
                                                                {child.totalAttendance}
                                                            </Badge>
                                                        </td>

                                                        <td>
                                                            <Badge color="badge-soft-info" className="badge-soft-info">
                                                                {child.totalAssignment}
                                                            </Badge>
                                                        </td>

                                                        <td>
                                                            <Badge color="badge-soft-warning" className="badge-soft-warning">
                                                                {child.totalProject}
                                                            </Badge>
                                                        </td>

                                                        <td>
                                                            <Badge color={`badge-soft-${getPerformanceColor(child.scorePercentage)}`} className={`badge-soft-${getPerformanceColor(child.scorePercentage)}`}>
                                                                <i className="mdi mdi-star me-1"></i>
                                                                {child.scorePercentage}%
                                                            </Badge>
                                                        </td>

                                                        <td>
                                                            <div className="d-flex gap-3">
                                                                <Link
                                                                    className="text-success"
                                                                    to="#"
                                                                    onClick={() => openChildDetails(child)}
                                                                >
                                                                    <i className="mdi mdi-eye font-size-18"></i>
                                                                </Link>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                    {filteredChildren.length > 0 && (
                                        <PaginationControls
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            totalItems={totalItems}
                                            pageSize={pageSize}
                                            showAll={showAll}
                                            handlePageChange={handlePageChange}
                                            handlePageSizeChange={handlePageSizeChange}
                                            goToNextPage={goToNextPage}
                                            goToPreviousPage={goToPreviousPage}
                                            canPreviousPage={canPreviousPage}
                                            canNextPage={canNextPage}
                                            paginatedDataLength={paginatedDataLength}
                                        />
                                    )}

                                    {/* Child Details Modal */}
                                    <Modal isOpen={detailsModal} toggle={closeChildDetails} size="lg">
                                        <ModalHeader toggle={closeChildDetails}>
                                            Evaluation Details - {selectedChild.firstName} {selectedChild.lastName}
                                        </ModalHeader>
                                        <ModalBody>
                                            {selectedChild && selectedChild.childId && (
                                                <div>
                                                    {/* Child Basic Info */}
                                                    <Row className="mb-4">
                                                        <Col md={3}>
                                                            <div className="mb-3">
                                                                <Label className="text-muted font-size-12">Child Name</Label>
                                                                <p className="mb-0 font-size-14 fw-bold">
                                                                    {selectedChild.firstName} {selectedChild.lastName}
                                                                </p>
                                                            </div>
                                                        </Col>
                                                        <Col md={3}>
                                                            <div className="mb-3">
                                                                <Label className="text-muted font-size-12">Level</Label>
                                                                <p className="mb-0 font-size-14 fw-bold">{selectedChild.level}</p>
                                                            </div>
                                                        </Col>
                                                        <Col md={3}>
                                                            <div className="mb-3">
                                                                <Label className="text-muted font-size-12">Teacher</Label>
                                                                <p className="mb-0 font-size-14 fw-bold">{selectedChild.teacher?.name}</p>
                                                            </div>
                                                        </Col>
                                                        <Col md={3}>
                                                            <div className="mb-3">
                                                                <Label className="text-muted font-size-12">Status</Label>
                                                                <div>
                                                                    <Badge color={`badge-soft-${getStatusColor(selectedChild.isActive)}`} className={`badge-soft-${getStatusColor(selectedChild.isActive)}`}>
                                                                        {selectedChild.isActive ? "Active" : "Inactive"}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    {/* Summary Cards */}
                                                    <Row className="mb-4">
                                                        <Col md={6}>
                                                            <Card className="border border-primary">
                                                                <CardBody className="text-center">
                                                                    <h4 className="text-primary mb-1">{selectedChild.totalScore}</h4>
                                                                    <p className="text-muted mb-0">
                                                                        Total Score <span className="text-muted">/ {selectedChild.totalPossibleScore}</span>
                                                                    </p>
                                                                    <small className="text-muted">({selectedChild.scorePercentage}%)</small>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Card className="border border-success">
                                                                <CardBody className="text-center">
                                                                    <h4 className="text-success mb-1">{selectedChild.completionPercentage}%</h4>
                                                                    <p className="text-muted mb-0">Completion Rate</p>
                                                                    <small className="text-muted">{selectedChild.weeksCompleted}/8 weeks</small>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                    </Row>

                                                    {/* Weekly Performance */}
                                                    <div className="mb-3">
                                                        <Label className="text-muted font-size-12">Weekly Performance Breakdown</Label>
                                                    </div>
                                                    <div className="table-responsive" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                                        <table className="table table-sm table-bordered">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th className="text-center">Week</th>
                                                                    <th className="text-center">Attendance</th>
                                                                    <th className="text-center">Assignment</th>
                                                                    <th className="text-center">Project</th>
                                                                    <th className="text-center">Week Score</th>
                                                                    <th className="text-center">Status</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {selectedChild.weeklyScores?.map((week) => (
                                                                    <tr key={week.week} className={week.completed ? "table-success" : ""}>
                                                                        <td className="text-center font-weight-bold">
                                                                            <Badge color="badge-soft-primary" className="badge-soft-primary">Week {week.week}</Badge>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            {week.attendance !== null ? (
                                                                                <Badge color="badge-soft-success" className="badge-soft-success">
                                                                                    {week.attendance}/1
                                                                                </Badge>
                                                                            ) : (
                                                                                <span className="text-muted">-</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="text-center">
                                                                            {week.assignment !== null ? (
                                                                                <Badge color="badge-soft-info" className="badge-soft-info">
                                                                                    {week.assignment}/4
                                                                                </Badge>
                                                                            ) : (
                                                                                <span className="text-muted">-</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="text-center">
                                                                            {week.week === 8 ? (
                                                                                week.project !== null ? (
                                                                                    <Badge color="badge-soft-warning" className="badge-soft-warning">
                                                                                        {week.project}/60
                                                                                    </Badge>
                                                                                ) : (
                                                                                    <span className="text-muted">-</span>
                                                                                )
                                                                            ) : (
                                                                                <span className="text-muted">N/A</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="text-center font-weight-bold text-primary">
                                                                            <Badge color="badge-soft-primary" className="badge-soft-primary">
                                                                                {week.weekScore}
                                                                            </Badge>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            {week.completed ? (
                                                                                <i className="mdi mdi-check-circle text-success font-size-16"></i>
                                                                            ) : (
                                                                                <i className="mdi mdi-clock text-warning font-size-16"></i>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Additional Metrics */}
                                                    <Row className="mt-4">
                                                        <Col md={4}>
                                                            <Card className="border-0 bg-light">
                                                                <CardBody className="text-center py-3">
                                                                    <p className="text-muted font-size-12 mb-1">Average Score</p>
                                                                    <h5 className="text-primary mb-0">{selectedChild.averageScore?.toFixed(1)}</h5>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Card className="border-0 bg-light">
                                                                <CardBody className="text-center py-3">
                                                                    <p className="text-muted font-size-12 mb-1">Evaluations Count</p>
                                                                    <h5 className="text-success mb-0">{selectedChild.evaluationsCount}</h5>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Card className="border-0 bg-light">
                                                                <CardBody className="text-center py-3">
                                                                    <p className="text-muted font-size-12 mb-1">Last Evaluation</p>
                                                                    <h6 className="text-info mb-0">
                                                                        {selectedChild.lastEvaluation ?
                                                                            new Date(selectedChild.lastEvaluation).toLocaleDateString() :
                                                                            'N/A'}
                                                                    </h6>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            )}
                                        </ModalBody>
                                    </Modal>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
};

export default withAuth(PartnerReport);